import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Users, ShieldAlert, Building2, Landmark, ScrollText,
  Banknote, Car, Building, HardHat, KeyRound, Home, Briefcase, Scale,
  Lightbulb, Cpu, ShoppingCart, FileSignature,
} from "lucide-react";
import { MaskedReveal, FadeUp } from "@/components/motion/Reveal";
import { Marquee } from "@/components/motion/Marquee";
import { PRACTICE_AREAS } from "@/data/site";

const ICONS = {
  "family-law": Users,
  "criminal-law": ShieldAlert,
  "corporate-law": Building2,
  "civil-litigation": Landmark,
  "testamentary-succession": ScrollText,
  "drt-sarfaesi": Banknote,
  "mact": Car,
  "cooperative-housing-society": Building,
  "redevelopment": HardHat,
  "conveyance-property-transfer": KeyRound,
  "rent-control": Home,
  "labour-employment": Briefcase,
  "arbitration": Scale,
  "intellectual-property": Lightbulb,
  "it-cyber-law": Cpu,
  "consumer-protection": ShoppingCart,
  "document-registration": FileSignature,
};

export default function PracticeAreas() {
  return (
    <div data-testid="practice-areas-page">
      <section className="pt-40 pb-14">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-6">Our Practice</div>
          <MaskedReveal
            as="h1"
            className="serif text-6xl md:text-[7.5vw] leading-[0.94]"
            lines={["A full-service", <>legal <em className="italic text-gold">practice.</em></>]}
          />
          <FadeUp delay={0.35} className="max-w-2xl mt-10 text-[hsl(var(--muted-foreground))] leading-relaxed">
            Our multi-speciality firm provides counsel and representation across the following segments of the legal field — from litigation to advisory.
          </FadeUp>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {PRACTICE_AREAS.map((p, i) => {
              const Icon = ICONS[p.slug] || Scale;
              return (
                <FadeUp key={p.slug} delay={(i % 3) * 0.06}>
                  <Link
                    to={`/practice-areas/${p.slug}`}
                    className="group relative flex flex-col h-full bg-[hsl(var(--card))] border hairline p-7 md:p-8 transition-colors duration-500 hover:bg-navy"
                    data-testid={`pa-card-${p.slug}`}
                  >
                    <div className="flex items-start justify-between mb-8">
                      <span className="h-12 w-12 rounded-full border hairline grid place-items-center text-[hsl(var(--navy))] group-hover:text-gold group-hover:border-white/30 transition-colors">
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                      </span>
                      <ArrowUpRight className="h-5 w-5 text-[hsl(var(--muted-foreground))] group-hover:text-gold transition-colors -translate-x-1 translate-y-1 group-hover:translate-x-0 group-hover:translate-y-0" />
                    </div>
                    <h2 className="serif text-2xl md:text-[1.7rem] leading-tight mb-3 text-[hsl(var(--navy))] group-hover:text-[hsl(var(--ivory))] transition-colors">
                      {p.title}
                    </h2>
                    <p className="text-sm leading-relaxed text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--ivory))]/70 transition-colors flex-1">
                      {p.lede}
                    </p>
                    <span className="mt-6 mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--navy))] group-hover:text-gold transition-colors inline-flex items-center gap-2">
                      Read more <ArrowUpRight className="h-3.5 w-3.5" />
                    </span>
                  </Link>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      <Marquee items={["Committed to Justice", "Dedicated to Excellence", "Litigation · Advisory · Compliance", "Established in Mumbai"]} />
    </div>
  );
}
